import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

// =============================================================================
// useGhlUserNameMap — org-scoped GHL user id → display name
// =============================================================================
// Resolves a GHL user id (e.g. owner_at_send on ghl_messages) to a display name
// for DM bubble badges. Source of truth for DM display names is ghl_users (NOT
// profiles) — see CLAUDE.md "ghl_users for DM display names" + the fired-setter
// ownership handoff. Returns { [ghl_user_id]: "First Last" }.
//
// Module-level cache keyed by orgId so remounting a modal doesn't refetch the
// whole team on every open. GHL user rosters are small (100+ accounts), so one
// unfiltered org read is cheap and rarely changes within a session.
// =============================================================================

const nameMapCache = new Map<string, Record<string, string>>();

export function useGhlUserNameMap(orgId: string | null | undefined): Record<string, string> {
  const [map, setMap] = useState<Record<string, string>>(() =>
    orgId && nameMapCache.has(orgId) ? nameMapCache.get(orgId)! : {}
  );
  // Track the orgId the current map reflects. When orgId changes we adjust the
  // map DURING render (React's recommended pattern) instead of setState-in-effect,
  // which trips react-hooks set-state-in-effect. The effect below only does the
  // async fetch for an org we have not cached yet.
  const [reflectedOrgId, setReflectedOrgId] = useState(orgId);
  if (orgId !== reflectedOrgId) {
    setReflectedOrgId(orgId);
    setMap(orgId && nameMapCache.has(orgId) ? nameMapCache.get(orgId)! : {});
  }

  useEffect(() => {
    if (!orgId || !supabase) return;
    if (nameMapCache.has(orgId)) return; // already cached and reflected via render-time adjust
    let cancelled = false;
    supabase
      .from("ghl_users")
      .select("ghl_user_id, first_name, last_name")
      .eq("organization_id", orgId)
      .then(({ data }) => {
        if (cancelled || !data) return;
        const next: Record<string, string> = {};
        for (const u of data) {
          if (!u.ghl_user_id) continue;
          next[u.ghl_user_id] = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
        }
        nameMapCache.set(orgId, next);
        setMap(next);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  return map;
}
